import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ExternalLink, Mail, Copy, MoreHorizontal, ChevronLeft, ChevronRight, Camera, MapPin, Calendar, ScanLine, Edit, Plus, Loader2, AlertCircle, Trash2, Unlink, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { getStatusKey, type MemorialStatus } from '@/utils/memorialStatus';
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { posthog } from '@/lib/posthog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditMemorialDialog } from '@/components/portal/EditMemorialDialog';

interface InstallationData {
  id: string;
  photo_url: string;
  thumbnail_url: string | null;
  latitude: number | null;
  longitude: number | null;
  location_accuracy: number | null;
  captured_at: string | null;
}

interface MemorialWithToken {
  id: string;
  first_name: string;
  last_name: string | null;
  user_id: string | null;
  slug: string | null;
  assigned_email: string | null;
  assigned_phone: string | null;
  assigned_at: string | null;
  last_invitation_email_sent_at: string | null;
  last_invitation_email_status: string | null;
  token: string;
  claimed: boolean;
  has_installation_photo: boolean;
  installation: InstallationData | null;
  intended_cemetery_name?: string | null;
  intended_city?: string | null;
  intended_latitude?: number | null;
  intended_longitude?: number | null;
  headstone_type_id?: string | null;
  headstone_type_name?: string | null;
  stone_count?: string | null;
  is_double_memorial?: boolean | null;
  person2_first_name?: string | null;
  person2_last_name?: string | null;
  is_installed: boolean;
  activated_at: string | null;
  is_active: boolean | null;
  short_code: string | null;
  client_email: string | null;
  contact_phone: string | null;
}

type StatusFilter = 'all' | MemorialStatus;

const ITEMS_PER_PAGE = 20;

const PortalMemorials = () => {
  const navigate = useNavigate();
  const { company, isLoading: authLoading } = usePortalAuth();
  const { isReadOnly } = useReadOnly();
  const [memorials, setMemorials] = useState<MemorialWithToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedInstallation, setSelectedInstallation] = useState<MemorialWithToken | null>(null);
  const [editingMemorial, setEditingMemorial] = useState<MemorialWithToken | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createCemeteryName, setCreateCemeteryName] = useState<string | null>(null);
  const [createCity, setCreateCity] = useState<string | null>(null);
  const [createLatitude, setCreateLatitude] = useState<number | null>(null);
  const [createLongitude, setCreateLongitude] = useState<number | null>(null);
  const [createHeadstoneTypeId, setCreateHeadstoneTypeId] = useState('');
  const [createStoneCount, setCreateStoneCount] = useState<'single' | 'double'>('single');
  const [createNeedsHelp, setCreateNeedsHelp] = useState(false);
  const [createIsDoubleMemorial, setCreateIsDoubleMemorial] = useState(false);
  const [createPerson2FirstName, setCreatePerson2FirstName] = useState('');
  const [createPerson2LastName, setCreatePerson2LastName] = useState('');
  const [createPerson2HasDifferentLastName, setCreatePerson2HasDifferentLastName] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreatingMemorial, setIsCreatingMemorial] = useState(false);
  const [memorialToDelete, setMemorialToDelete] = useState<MemorialWithToken | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [memorialToUnlink, setMemorialToUnlink] = useState<MemorialWithToken | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const fetchMemorials = useCallback(async () => {
    if (!company?.id) return;

    setIsLoading(true);
    try {
      // First get memorials for this company
      const { data: memorialsData, error: memorialsError } = await supabase
        .from('memorials')
        .select('id, first_name, last_name, user_id, slug, intended_cemetery_name, intended_city, intended_latitude, intended_longitude, headstone_type_id, stone_count, is_double_memorial, person2_first_name, person2_last_name, is_installed, activated_at, is_active, short_code, client_email, contact_phone')
        .eq('monument_company_id', company.id)
        .or('is_active.eq.true,is_active.is.null');

      if (memorialsError) throw memorialsError;

      if (!memorialsData || memorialsData.length === 0) {
        setMemorials([]);
        setTotalCount(0);
        setIsLoading(false);
        return;
      }

      // Get tokens for these memorials
      const memorialIds = memorialsData.map(m => m.id);
      const { data: tokensData, error: tokensError } = await supabase
        .from('memorial_tokens')
        .select('memorial_id, assigned_email, assigned_phone, assigned_at, last_invitation_email_sent_at, last_invitation_email_status, token, claimed')
        .in('memorial_id', memorialIds)
        .eq('token_type', 'b2b');

      if (tokensError) throw tokensError;

      // Get full installation data for these memorials
      const { data: installationsData } = await supabase
        .from('monument_installations')
        .select('id, memorial_id, photo_url, thumbnail_url, latitude, longitude, location_accuracy, captured_at')
        .in('memorial_id', memorialIds);

      // Get headstone type names for memorials that have one
      const headstoneTypeIds = [...new Set(memorialsData.filter(m => m.headstone_type_id).map(m => m.headstone_type_id!))];
      const headstoneTypeMap = new Map<string, string>();
      if (headstoneTypeIds.length > 0) {
        const { data: headstoneTypesData } = await supabase
          .from('headstone_types')
          .select('id, name')
          .in('id', headstoneTypeIds);
        headstoneTypesData?.forEach(ht => headstoneTypeMap.set(ht.id, ht.name));
      }

      // Create a map of memorial_id to installation data
      const installationsMap = new Map<string, InstallationData>();
      installationsData?.forEach(inst => {
        installationsMap.set(inst.memorial_id, {
          id: inst.id,
          photo_url: inst.photo_url,
          thumbnail_url: inst.thumbnail_url,
          latitude: inst.latitude ? Number(inst.latitude) : null,
          longitude: inst.longitude ? Number(inst.longitude) : null,
          location_accuracy: inst.location_accuracy ? Number(inst.location_accuracy) : null,
          captured_at: inst.captured_at,
        });
      });

      // Merge data
      let combined: MemorialWithToken[] = memorialsData.map(memorial => {
        const token = tokensData?.find(t => t.memorial_id === memorial.id);
        const installation = installationsMap.get(memorial.id) || null;
        return {
          ...memorial,
          assigned_email: token?.assigned_email || null,
          assigned_phone: token?.assigned_phone || null,
          assigned_at: token?.assigned_at || null,
          last_invitation_email_sent_at: token?.last_invitation_email_sent_at || null,
          last_invitation_email_status: token?.last_invitation_email_status || null,
          token: token?.token || '',
          claimed: token?.claimed || false,
          has_installation_photo: !!installation,
          installation,
          headstone_type_name: memorial.headstone_type_id ? headstoneTypeMap.get(memorial.headstone_type_id) || null : null,
        };
      });

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        combined = combined.filter(m => 
          m.first_name.toLowerCase().includes(query) ||
          (m.last_name?.toLowerCase().includes(query)) ||
          (m.assigned_email?.toLowerCase().includes(query)) ||
          (m.assigned_phone?.includes(query))
        );
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        combined = combined.filter(m => getStatusKey(m) === statusFilter);
      }

      // Sort by assigned_at desc, nulls last
      combined.sort((a, b) => {
        if (!a.assigned_at && !b.assigned_at) return 0;
        if (!a.assigned_at) return 1;
        if (!b.assigned_at) return -1;
        return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
      });

      setTotalCount(combined.length);

      // Paginate
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const paginated = combined.slice(startIndex, startIndex + ITEMS_PER_PAGE);

      setMemorials(paginated);
    } catch (error) {
      console.error('Error fetching memorials:', error);
      toast.error('Failed to load memorials');
    } finally {
      setIsLoading(false);
    }
  }, [company?.id, searchQuery, statusFilter, currentPage]);

  useEffect(() => {
    if (company?.id) {
      fetchMemorials();
    }
  }, [company?.id, fetchMemorials]);

  const { data: headstoneTypes } = useQuery({
    queryKey: ['headstone-types-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('headstone_types')
        .select('id, name, applicable_stone_count')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const resetCreateForm = () => {
    setCreateFirstName('');
    setCreateLastName('');
    setCreateEmail('');
    setCreatePhone('');
    setCreateCemeteryName(null);
    setCreateCity(null);
    setCreateLatitude(null);
    setCreateLongitude(null);
    setCreateHeadstoneTypeId('');
    setCreateStoneCount('single');
    setCreateNeedsHelp(false);
    setCreateIsDoubleMemorial(false);
    setCreatePerson2FirstName('');
    setCreatePerson2LastName('');
    setCreatePerson2HasDifferentLastName(false);
    setCreateError(null);
  };

  const handleCreateMemorial = async () => {
    if (!createFirstName.trim()) { setCreateError('First name is required'); return; }
    if (!createEmail.trim() && !createPhone.trim()) { setCreateError('Please enter a phone number or email address'); return; }
    if (createEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(createEmail.trim())) { setCreateError('Please enter a valid email address'); return; }
    }
    if (!createHeadstoneTypeId) { setCreateError('Please select a headstone type'); return; }

    setIsCreatingMemorial(true);
    setCreateError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-memorial', {
        body: {
          firstName: createFirstName.trim(),
          lastName: createLastName.trim() || undefined,
          clientEmail: createEmail.trim() || undefined,
          clientPhone: createPhone.trim() || undefined,
          intendedCemeteryName: createCemeteryName || createCity || null,
          intendedCity: createCity,
          intendedLatitude: createLatitude,
          intendedLongitude: createLongitude,
          headstoneTypeId: createHeadstoneTypeId,
          stoneCount: createStoneCount,
          monumentCompanyId: company!.id,
          isDoubleMemorial: createIsDoubleMemorial,
          person2FirstName: createIsDoubleMemorial ? createPerson2FirstName.trim() || undefined : undefined,
          person2LastName: createIsDoubleMemorial && createPerson2HasDifferentLastName
            ? createPerson2LastName.trim() || undefined
            : undefined,
        },
      });
      if (error) {
        let message = 'Failed to create memorial';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try { const body = await error.context.json(); if (body?.error) message = body.error; } catch { /* ignore parse error */ }
        }
        throw new Error(message);
      }
      posthog.capture('memorial_created', {
        memorial_id: data?.memorialId ?? null,
        is_b2b: true,
        purpose: null,
        monument_company_id: company!.id,
      });
      const channel = createPhone.trim() ? 'text' : createEmail.trim() ? 'email' : '';
      toast.success(channel ? `Memorial created. Activation ${channel} sent.` : 'Memorial created.');
      setShowCreateForm(false);
      resetCreateForm();
      fetchMemorials();
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create memorial');
    } finally {
      setIsCreatingMemorial(false);
    }
  };

  // Surfaces "Invite sent 3h ago · delivered" / "bounced" so staff know what happened
  const renderInvitationStatus = (memorial: MemorialWithToken) => {
    if (!memorial.last_invitation_email_sent_at) return null;
    const sentText = formatDistanceToNow(new Date(memorial.last_invitation_email_sent_at), { addSuffix: true });
    const status = memorial.last_invitation_email_status;
    const isProblem = status === 'bounced' || status === 'complained';
    return (
      <div className={`flex items-center gap-2 text-xs ${isProblem ? 'text-destructive' : 'text-muted-foreground'}`}>
        {isProblem && <AlertTriangle className="h-3.5 w-3.5" />}
        <span>
          Invite sent {sentText}
          {status ? ` · ${status}` : ''}
        </span>
      </div>
    );
  };

  const getStatusBadge = (memorial: MemorialWithToken) => {
    const status = getStatusKey(memorial);
    switch (status) {
      case 'uninstalled':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800 hover:bg-orange-100">Uninstalled</Badge>;
      case 'activated':
        return <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-100">Activated</Badge>;
      case 'installed':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">Installed</Badge>;
      case 'active':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
    }
  };

  const handleResendInvite = async (memorial: MemorialWithToken) => {
    // Edit Memorial updates the memorial's client_email / contact_phone, but the
    // invitation is sent to memorial_tokens.assigned_email / assigned_phone.
    // Send to whatever the portal currently shows for the family and sync it
    // onto the invitation via the edit flow, so a corrected email actually
    // reaches them instead of the stale address from the original invite.
    const email = memorial.client_email || memorial.assigned_email || undefined;
    const phone = memorial.contact_phone || memorial.assigned_phone || undefined;
    if (!email && !phone) return;

    try {
      const { error } = await supabase.functions.invoke('assign-b2b-memorial', {
        body: {
          memorialId: memorial.id,
          email,
          phone,
          edit: true,
        },
      });

      if (error) throw error;
      const channel = phone ? 'text' : 'email';
      toast.success(`Activation ${channel} resent successfully`);
      fetchMemorials();
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error('Failed to resend invite');
    }
  };

  const handleCopyUrl = (memorial: MemorialWithToken) => {
    const url = `${window.location.origin}/memorial/${memorial.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Memorial URL copied to clipboard');
  };

  const handleViewMemorial = (memorial: MemorialWithToken) => {
    window.open(`/memorial/${memorial.id}`, '_blank');
  };

  const handleDeleteMemorial = async (memorial: MemorialWithToken) => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('memorials')
        .update({ is_active: false })
        .eq('id', memorial.id);
      if (error) throw error;
      toast.success('Memorial deleted');
      setMemorialToDelete(null);
      fetchMemorials();
    } catch (error) {
      console.error('Error deleting memorial:', error);
      toast.error('Failed to delete memorial');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnlinkMedallion = async (memorial: MemorialWithToken) => {
    if (!memorial.short_code) {
      toast.error('No medallion linked to this memorial');
      return;
    }
    setIsUnlinking(true);
    try {
      const { error } = await supabase.functions.invoke('unlink-medallion', {
        body: { shortCode: memorial.short_code, memorialId: memorial.id },
      });
      if (error) {
        let message = 'Failed to unlink medallion';
        if (error instanceof FunctionsHttpError && error.context?.json) {
          try { const body = await error.context.json(); if (body?.error) message = body.error; } catch { /* ignore */ }
        }
        throw new Error(message);
      }
      toast.success('Medallion unlinked successfully');
      setMemorialToUnlink(null);
      fetchMemorials();
    } catch (err: unknown) {
      console.error('Error unlinking medallion:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to unlink medallion');
    } finally {
      setIsUnlinking(false);
    }
  };

  const formatCoordinate = (value: number, isLatitude: boolean) => {
    const direction = isLatitude 
      ? (value >= 0 ? 'N' : 'S')
      : (value >= 0 ? 'E' : 'W');
    return `${Math.abs(value).toFixed(6)}° ${direction}`;
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  if (authLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Memorials</h1>
          <p className="text-muted-foreground mt-1">
            View and manage memorials assigned to your company
          </p>
        </div>
        {!isReadOnly && (
          <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Memorial
          </Button>
        )}
      </div>

      {/* Create Memorial Dialog */}
      <Dialog open={showCreateForm} onOpenChange={(open) => { if (!open) { setShowCreateForm(false); resetCreateForm(); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Memorial</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>First Name <span className="text-destructive">*</span></Label>
              <Input value={createFirstName} onChange={(e) => setCreateFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div className="space-y-2">
              <Label>Last Name</Label>
              <Input value={createLastName} onChange={(e) => setCreateLastName(e.target.value)} placeholder="Last name" />
            </div>
            <div className="space-y-2">
              <Label>Companion Memorial & Headstone</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={createIsDoubleMemorial === false ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => {
                    setCreateIsDoubleMemorial(false);
                    setCreatePerson2FirstName('');
                    setCreatePerson2LastName('');
                    setCreatePerson2HasDifferentLastName(false);
                    setCreateStoneCount('single');
                    const stillValid = headstoneTypes?.some(
                      (ht) => ht.id === createHeadstoneTypeId && (ht.applicable_stone_count === 'both' || ht.applicable_stone_count === 'single')
                    );
                    if (!stillValid) setCreateHeadstoneTypeId('');
                  }}
                >
                  No
                </Button>
                <Button
                  type="button"
                  variant={createIsDoubleMemorial === true ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => {
                    setCreateIsDoubleMemorial(true);
                    setCreateStoneCount('double');
                    const stillValid = headstoneTypes?.some(
                      (ht) => ht.id === createHeadstoneTypeId && (ht.applicable_stone_count === 'both' || ht.applicable_stone_count === 'double')
                    );
                    if (!stillValid) setCreateHeadstoneTypeId('');
                  }}
                >
                  Yes
                </Button>
              </div>
            </div>
            {createIsDoubleMemorial && (
              <>
                <div className="space-y-2">
                  <Label>Second Person's First Name</Label>
                  <Input value={createPerson2FirstName} onChange={(e) => setCreatePerson2FirstName(e.target.value)} placeholder="First name of second person" />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="create-different-last-name" className="text-sm font-normal text-muted-foreground cursor-pointer">
                    They had different last names
                  </Label>
                  <Switch
                    id="create-different-last-name"
                    checked={createPerson2HasDifferentLastName}
                    onCheckedChange={(checked) => {
                      setCreatePerson2HasDifferentLastName(checked);
                      if (!checked) setCreatePerson2LastName('');
                    }}
                  />
                </div>
                {createPerson2HasDifferentLastName && (
                  <div className="space-y-2">
                    <Label>Second Person's Last Name</Label>
                    <Input value={createPerson2LastName} onChange={(e) => setCreatePerson2LastName(e.target.value)} placeholder="Last name of second person" />
                  </div>
                )}
              </>
            )}
            <div className="space-y-2">
              <Label>Does the client need help setting up the memorial?</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={createNeedsHelp === false ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setCreateNeedsHelp(false)}
                >
                  No
                </Button>
                <Button
                  type="button"
                  variant={createNeedsHelp === true ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setCreateNeedsHelp(true)}
                >
                  Yes
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{createNeedsHelp ? "Helper's Email" : "Client Email"}</Label>
              <Input type="email" value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} placeholder={createNeedsHelp ? "Helper's email (e.g. son or daughter)" : "client@example.com"} />
              <p className="text-xs text-muted-foreground">
                {createNeedsHelp
                  ? "We'll send the activation email to this person to set up the memorial"
                  : "Activation email will be sent if provided"}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Client Phone</Label>
              <Input type="tel" value={createPhone} onChange={(e) => setCreatePhone(e.target.value)} placeholder="(555) 123-4567" />
              {!createEmail.trim() && !createPhone.trim() && (
                <p className="text-xs text-amber-600">At least one of phone or email is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Headstone Type <span className="text-destructive">*</span></Label>
              <Select
                value={createHeadstoneTypeId}
                onValueChange={setCreateHeadstoneTypeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select headstone type..." />
                </SelectTrigger>
                <SelectContent>
                  {headstoneTypes
                    ?.filter((ht) => !createStoneCount || ht.applicable_stone_count === 'both' || ht.applicable_stone_count === createStoneCount)
                    .map((ht) => (
                      <SelectItem key={ht.id} value={ht.id}>{ht.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {createError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowCreateForm(false); resetCreateForm(); }}>Cancel</Button>
              <Button onClick={handleCreateMemorial} disabled={isCreatingMemorial}>
                {isCreatingMemorial ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : 'Create Memorial'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value: StatusFilter) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="uninstalled">Uninstalled</SelectItem>
            <SelectItem value="installed">Installed</SelectItem>
            <SelectItem value="activated">Activated</SelectItem>
            <SelectItem value="active">Active</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
              <div className="flex gap-2 pt-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
              </div>
            </div>
          ))
        ) : memorials.length === 0 ? (
          <div className="border rounded-lg p-8 text-center text-muted-foreground">
            {searchQuery || statusFilter !== 'all' 
              ? 'No memorials match your filters'
              : 'No memorials assigned to your company yet'}
          </div>
        ) : (
          memorials.map((memorial) => (
            <div key={memorial.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-foreground">
                    {buildMemorialDisplayName(memorial)}
                  </h3>
                  {memorial.has_installation_photo ? (
                    <button
                      onClick={() => setSelectedInstallation(memorial)}
                      className="text-green-600 hover:text-green-700 transition-colors"
                      title="View installation photo"
                    >
                      <Camera className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="text-amber-600" title="Not installed">
                      <ScanLine className="h-4 w-4" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(memorial)}
                </div>
              </div>
              
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span>{memorial.client_email || memorial.assigned_email || '—'}</span>
                </div>
                {renderInvitationStatus(memorial)}
                {memorial.intended_cemetery_name && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{memorial.intended_cemetery_name}</span>
                  </div>
                )}
                {memorial.headstone_type_name && (
                  <div className="text-muted-foreground">
                    {memorial.headstone_type_name}
                  </div>
                )}
                {memorial.short_code && (
                  <div className="text-muted-foreground font-mono text-xs">
                    Medallion: {memorial.short_code}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewMemorial(memorial)}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View
                </Button>
                {memorial.is_installed && memorial.has_installation_photo && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedInstallation(memorial)}
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    Installation
                  </Button>
                )}
                {!isReadOnly && !memorial.is_installed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/portal/upload-photo')}
                  >
                    <ScanLine className="h-4 w-4 mr-1" />
                    Install
                  </Button>
                )}
                {!isReadOnly && !memorial.activated_at && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingMemorial(memorial)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
                {!isReadOnly && (memorial.assigned_email || memorial.assigned_phone) && !memorial.activated_at && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendInvite(memorial)}
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Resend
                  </Button>
                )}
                {!isReadOnly && memorial.is_installed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMemorialToUnlink(memorial)}
                  >
                    <Unlink className="h-4 w-4 mr-1" />
                    Unlink
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyUrl(memorial)}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy URL
                </Button>
                {!isReadOnly && !memorial.activated_at && !memorial.is_installed && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setMemorialToDelete(memorial)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Memorial Name</TableHead>
                <TableHead>Client Email</TableHead>
                <TableHead>Cemetery</TableHead>
                <TableHead>Headstone Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                  </TableRow>
                ))
              ) : memorials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'No memorials match your filters'
                      : 'No memorials assigned to your company yet'}
                  </TableCell>
                </TableRow>
              ) : (
                memorials.map((memorial) => (
                  <TableRow key={memorial.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {buildMemorialDisplayName(memorial)}
                        {memorial.has_installation_photo && (
                          <button
                            onClick={() => setSelectedInstallation(memorial)}
                            className="text-green-600 hover:text-green-700 transition-colors"
                            title="View installation photo"
                          >
                            <Camera className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div>{memorial.client_email || memorial.assigned_email || '—'}</div>
                      {renderInvitationStatus(memorial)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {memorial.intended_cemetery_name || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {memorial.headstone_type_name || '—'}
                    </TableCell>
                    <TableCell>{getStatusBadge(memorial)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewMemorial(memorial)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View Memorial
                          </DropdownMenuItem>
                          {memorial.is_installed && memorial.has_installation_photo && (
                            <DropdownMenuItem onClick={() => setSelectedInstallation(memorial)}>
                              <MapPin className="h-4 w-4 mr-2" />
                              View Installation
                            </DropdownMenuItem>
                          )}
                          {!isReadOnly && !memorial.is_installed && (
                            <DropdownMenuItem onClick={() => navigate('/portal/upload-photo')}>
                              <ScanLine className="h-4 w-4 mr-2" />
                              Install Medallion
                            </DropdownMenuItem>
                          )}
                          {!isReadOnly && !memorial.activated_at && (
                            <DropdownMenuItem onClick={() => setEditingMemorial(memorial)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Memorial
                            </DropdownMenuItem>
                          )}
                          {!isReadOnly && (memorial.assigned_email || memorial.assigned_phone) && !memorial.activated_at && (
                            <DropdownMenuItem onClick={() => handleResendInvite(memorial)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Resend Invite
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleCopyUrl(memorial)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy URL
                          </DropdownMenuItem>
                          {!isReadOnly && memorial.is_installed && (
                            <DropdownMenuItem onClick={() => setMemorialToUnlink(memorial)}>
                              <Unlink className="h-4 w-4 mr-2" />
                              Unlink Medallion
                            </DropdownMenuItem>
                          )}
                          {!isReadOnly && !memorial.activated_at && !memorial.is_installed && (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setMemorialToDelete(memorial)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} memorials
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Installation Details Dialog */}
      <Dialog open={!!selectedInstallation} onOpenChange={() => setSelectedInstallation(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Installation Photo
            </DialogTitle>
          </DialogHeader>
          
          {selectedInstallation?.installation && (
            <div className="space-y-4">
              {/* Memorial Name */}
              <div className="text-center pb-2 border-b">
                <p className="text-lg font-medium">
                  {buildMemorialDisplayName(selectedInstallation)}
                </p>
              </div>

              {/* Photo */}
              <div className="rounded-lg overflow-hidden bg-muted">
                <img
                  src={selectedInstallation.installation.photo_url}
                  alt={`Installation photo for ${selectedInstallation.first_name}`}
                  className="w-full h-auto max-h-[300px] object-contain"
                />
              </div>

              {/* Location Info */}
              {selectedInstallation.installation.latitude && selectedInstallation.installation.longitude ? (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-sm">Location</p>
                      <p className="text-sm text-muted-foreground font-mono">
                        {formatCoordinate(selectedInstallation.installation.latitude, true)},{' '}
                        {formatCoordinate(selectedInstallation.installation.longitude, false)}
                      </p>
                      {selectedInstallation.installation.location_accuracy && (
                        <p className="text-xs text-muted-foreground">
                          Accuracy: ~{Math.round(selectedInstallation.installation.location_accuracy)} meters
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Map Button */}
                  <a
                    href={`https://www.google.com/maps?q=${selectedInstallation.installation.latitude},${selectedInstallation.installation.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 w-full"
                  >
                    <img 
                      src="https://www.google.com/favicon.ico" 
                      alt="Google" 
                      className="h-4 w-4 mr-2"
                    />
                    Open in Google Maps
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Location not captured</p>
                </div>
              )}

              {/* Captured Date */}
              {selectedInstallation.installation.captured_at && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Calendar className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium text-sm">Captured</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedInstallation.installation.captured_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Memorial Dialog */}
      <EditMemorialDialog
        memorial={editingMemorial}
        isOpen={!!editingMemorial}
        onOpenChange={(open) => !open && setEditingMemorial(null)}
        onSuccess={() => {
          setEditingMemorial(null);
          fetchMemorials();
        }}
        headstoneTypes={headstoneTypes}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!memorialToDelete} onOpenChange={() => setMemorialToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Memorial?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove &ldquo;{buildMemorialDisplayName(memorialToDelete)}&rdquo; from your memorials list. This action can be undone by contacting support.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => memorialToDelete && handleDeleteMemorial(memorialToDelete)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Memorial'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unlink Medallion Confirmation Dialog */}
      <AlertDialog open={!!memorialToUnlink} onOpenChange={() => setMemorialToUnlink(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Medallion?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect medallion {memorialToUnlink?.short_code} from &ldquo;{buildMemorialDisplayName(memorialToUnlink)}&rdquo;. The medallion can then be reused for another memorial. The memorial content and family&apos;s work will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnlinking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => memorialToUnlink && handleUnlinkMedallion(memorialToUnlink)}
              disabled={isUnlinking}
            >
              {isUnlinking ? 'Unlinking...' : 'Unlink Medallion'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalMemorials;
